<template>
  <hb-section class="profile-header">
    <div class="profile-header__row">
      <b-avatar :text="initials" size="56" variant="light-primary" />

      <div class="profile-header__identity">
        <div class="profile-header__name-row">
          <h2 class="profile-header__name">{{ profile.name }}</h2>
          <b-badge v-if="isVerified" variant="light-success" pill>
            {{ $t('influencer_panel.verified') }}
          </b-badge>
          <b-badge
            v-if="profile.level"
            :variant="`light-${profile.level.badge_color || 'primary'}`"
          >
            {{ profile.level.name }}
            <span v-if="Number(profile.level.commission_bonus_pct) > 0">
              · +{{ profile.level.commission_bonus_pct }}%
            </span>
          </b-badge>
        </div>

        <div v-if="hasLevelBonus" class="profile-header__link-row">
          <feather-icon icon="TrendingUpIcon" size="14" />
          <span>{{ $t('influencer_panel.level_bonus', { pct: profile.level.commission_bonus_pct }) }}</span>
        </div>
      </div>

      <div class="profile-header__actions">
        <b-button variant="primary" size="sm" @click="$emit('generate-link')">
          <feather-icon icon="Link2Icon" size="14" class="mr-50" />
          {{ $t('influencer_panel.generate_link') }}
        </b-button>
      </div>
    </div>
  </hb-section>
</template>

<script>
import { BAvatar, BBadge, BButton } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'

/**
 * Cabecalho de identidade do influencer.
 *
 * Tres coisas que o dado nao entrega como o desenho supunha:
 *
 * - **Nao ha foto.** `Influencer` nao implementa `HasMedia` e nao existe coluna de avatar,
 *   entao o `b-avatar` fica com as iniciais em vez de inventar um endpoint de imagem.
 * - **"Verificada" nao existe no influencer.** O `verified_at` e por perfil social, e
 *   manual. O selo aqui e derivado: basta uma rede verificada.
 * - **Nao ha vitrine.** O desenho mostrava o link `hubee.com.br/i/{slug}` com copiar,
 *   compartilhar e "Ver vitrine". Essa rota nao existe no marketplace — cai no catch-all
 *   e vai para a Home. O bloco saiu daqui; o que o influencer divulga sao os links de
 *   campanha, que existem e sao rastreaveis. Volta quando a vitrine existir.
 */
export default {
  name: 'ProfileHeader',
  components: {
    BAvatar, BBadge, BButton, HbSection,
  },
  props: {
    profile: { type: Object, required: true },
  },
  computed: {
    initials() {
      return String(this.profile.name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0])
        .join('')
        .toUpperCase()
    },
    isVerified() {
      const profiles = this.profile.socialProfiles || this.profile.social_profiles || []

      return profiles.some(p => !!p.verified_at)
    },
    hasLevelBonus() {
      return !!this.profile.level && Number(this.profile.level.commission_bonus_pct) > 0
    },
  },
}
</script>

<style scoped>
.profile-header__row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

.profile-header__identity {
  flex: 1 1 260px;
  min-width: 0;
}

.profile-header__name-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.profile-header__name {
  margin: 0;
  font-size: var(--fs-card-title);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.profile-header__link-row {
  margin-top: 0.45rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.profile-header__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
</style>
